import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { notificationService } from "../../services/notificationService";
import { UserNotificationPreferences } from "../../types/Notifications";

export interface NotificationState {
  preferences: UserNotificationPreferences | null;
  followedPlayers: string[];
  isInitialized: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  preferences: null,
  followedPlayers: [],
  isInitialized: false,
  loading: false,
  error: null,
};

// Thunk pour initialiser les notifications
export const initializeNotifications = createAsyncThunk(
  "notifications/initialize",
  async (userId: string) => {
    const preferences = await notificationService.getNotificationPreferences(
      userId
    );

    // Seulement initialiser les notifications si l'utilisateur a explicitement activé les notifications push
    let token = null;
    if (preferences?.fcmToken) {
      console.log(
        "🔔 [DEBUG] Utilisateur a déjà activé les notifications push, initialisation du token"
      );
      token = await notificationService.initializeNotifications(userId);

      // Si un token a été obtenu lors de l'initialisation mais n'est pas dans les préférences,
      // on le sauvegarde dans les préférences
      if (token && (!preferences || !preferences.fcmToken)) {
        console.log(
          "🔔 [DEBUG] Token FCM obtenu lors de l'initialisation, sauvegarde dans les préférences"
        );
        await notificationService.saveNotificationPreferences(userId, {
          fcmToken: token,
        });
        // Récupérer les préférences mises à jour
        const updatedPreferences =
          await notificationService.getNotificationPreferences(userId);
        return { token, preferences: updatedPreferences };
      }
    } else {
      console.log(
        "🔔 [DEBUG] Utilisateur n'a pas activé les notifications push, pas d'initialisation automatique"
      );
    }

    return { token, preferences };
  }
);

// Thunk pour sauvegarder les préférences
export const saveNotificationPreferences = createAsyncThunk(
  "notifications/savePreferences",
  async ({
    userId,
    preferences,
  }: {
    userId: string;
    preferences: Partial<UserNotificationPreferences>;
  }) => {
    await notificationService.saveNotificationPreferences(userId, preferences);
    return await notificationService.getNotificationPreferences(userId);
  }
);

// Thunk pour basculer le suivi d'un joueur
export const toggleFollowedPlayer = createAsyncThunk(
  "notifications/toggleFollowedPlayer",
  async ({ userId, playerId }: { userId: string; playerId: string }) => {
    await notificationService.toggleFollowedPlayer(userId, playerId);
    const preferences = await notificationService.getNotificationPreferences(
      userId
    );
    return { preferences, playerId };
  }
);

// Thunk pour vérifier si un joueur est suivi
export const checkIfFollowingPlayer = createAsyncThunk(
  "notifications/checkIfFollowing",
  async ({ userId, playerId }: { userId: string; playerId: string }) => {
    const isFollowing = await notificationService.isFollowingPlayer(
      userId,
      playerId
    );
    return { playerId, isFollowing };
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPreferences: (
      state,
      action: PayloadAction<UserNotificationPreferences>
    ) => {
      state.preferences = action.payload;
      state.followedPlayers = action.payload.followedPlayers || [];
    },
    addFollowedPlayer: (state, action: PayloadAction<string>) => {
      if (!state.followedPlayers.includes(action.payload)) {
        state.followedPlayers.push(action.payload);
      }
    },
    removeFollowedPlayer: (state, action: PayloadAction<string>) => {
      state.followedPlayers = state.followedPlayers.filter(
        (id) => id !== action.payload
      );
    },
    resetNotifications: (state) => {
      state.preferences = null;
      state.followedPlayers = [];
      state.isInitialized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize notifications
      .addCase(initializeNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.isInitialized = true;
        if (action.payload.preferences) {
          state.preferences = action.payload.preferences;
          state.followedPlayers =
            action.payload.preferences.followedPlayers || [];
        }
      })
      .addCase(initializeNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          "Erreur lors de l'initialisation des notifications";
      })
      // Save preferences
      .addCase(saveNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.preferences = action.payload;
          state.followedPlayers = action.payload.followedPlayers || [];
        }
      })
      .addCase(saveNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          "Erreur lors de la sauvegarde des préférences";
      })
      // Toggle followed player
      .addCase(toggleFollowedPlayer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleFollowedPlayer.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.preferences) {
          state.preferences = action.payload.preferences;
          state.followedPlayers =
            action.payload.preferences.followedPlayers || [];
        }
      })
      .addCase(toggleFollowedPlayer.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Erreur lors de la modification du suivi";
      });
  },
});

export const {
  setLoading,
  setError,
  clearError,
  setPreferences,
  addFollowedPlayer,
  removeFollowedPlayer,
  resetNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
